<template>
  <v-container fluid class="px-12 py-4" v-if="loadingComplete">
    <billing-and-roi-builder></billing-and-roi-builder>
    <v-row>
      <v-col cols="8">
        <breadcrumbs></breadcrumbs>
        <h1 data-cy="buildStructureTitle">Chart - Proposed Structure</h1>
      </v-col>
      <v-col cols="4" class="text-right">
        <div class="mt-5">
          <v-btn
            @click="$router.push({ name: 'structureList' })"
            color="primary"
            text
            ><v-icon small class="mr-2" data-cy="listView_btn"
              >fas fa-list</v-icon
            >
            List View</v-btn
          >
        </div>
      </v-col>
    </v-row>
    <v-divider class="mb-4"></v-divider>
    <v-row>
      <v-col cols="8">
        <h2 class="title" data-cy="organizationName">
          {{ organization.name }}
          <span v-if="organization.serviceAreaId"
            >(SA{{ organization.serviceAreaId }})</span
          >
        </h2>
        <div class="body-2" v-if="organization.abbreviation">
          Abbreviation: {{ organization.abbreviation }}
        </div>
        <div v-if="!organization.serviceAreaId">
          <v-alert class="mt-5" dense outlined type="error"
            >There is no Service Area ID assigned to this organization.</v-alert
          >
        </div>
      </v-col>
      <v-col cols="4" class="text-right">
        <v-btn
          @click="
            $router.push({
              name: 'structureOrganizationForm',
              query: { redirect: 'structureChart' },
            })
          "
          :disabled="isDisabled"
          class="mt-3"
          color="primary"
          data-cy="organizationEditBtn"
          outlined
          v-if="canEditOrganization"
        >
          Edit
        </v-btn>
      </v-col>
      <v-col>
        <div v-if="canEditOrganization">
          <lock-structure-checkbox
            @structureLockToggled="reloadData"
          ></lock-structure-checkbox>
        </div>
      </v-col>
    </v-row>
    <v-divider class="my-5"></v-divider>
    <div>
      <v-card
        class="och-card--structure body-1 py-2 elevation-0 darken-2"
        color="success"
        dark
        shaped
      >
        <div class="font-weight-bold">Location</div>
      </v-card>

      <v-card
        class="och-card--structure body-1 py-2 elevation-0"
        color="primary"
        dark
        shaped
      >
        <div class="font-weight-bold">Department</div>
      </v-card>
    </div>

    <v-divider class="my-5"></v-divider>

    <div
      :key="location.locationId"
      data-cy="locationRow"
      v-for="location in locationList"
    >
      <div class="d-flex align-stretch">
        <div>
          <v-card
            class="och-card--structure caption pt-2 elevation-0 darken-2"
            color="success"
            dark
            shaped
            tile
          >
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <div v-on="on">
                  <div class="font-weight-bold subtitle-1">
                    {{ location.name }}
                  </div>
                  <div
                    class="font-weight-bold subtitle-1"
                    v-if="location.locationNumber"
                  >
                    {{ location.locationNumber }}
                  </div>
                  <div v-else>
                    <span class="font-italic subtitle-1"
                      >(Missing Location #)</span
                    >
                  </div>
                  <div
                    class="font-weight-bold subtitle-2"
                    v-if="location.abbreviation"
                  >
                    {{ `${location.abbreviation}`.toUpperCase() }}
                  </div>
                  <div v-else class="font-italic subtitle-1">
                    (Missing Abbreviation)
                  </div>
                </div>
              </template>
              <span> Created on {{ formatTimestamp(location.created) }} </span>
            </v-tooltip>
            <v-spacer> </v-spacer>
            <v-card-actions
              class="py-0 pb-2 pt-2 mt-3 success darken-4"
              position="absolute"
            >
              <v-btn
                :data-cy="`locationEditBtn${location.locationId}`"
                :to="{
                  name: 'structureLocationForm',
                  params: { locationId: location.locationId },
                  query: { redirect: 'structureChart' },
                }"
                color="success darken-4"
                elevation="0"
                small
                >edit</v-btn
              >
              <v-spacer></v-spacer>

              <DeleteFkEntityDialog
                :data-cy="`deleteBtn--location${location.locationId}-noDepartments`"
                :disabled="deleteLocationIsDisabled(location.locationId)"
                id
                :fkEntityId="location.locationId"
                :fkEntityLabel="location.name"
                :outlined="false"
                :text="true"
                color="white"
                fkEntityName="location"
                v-if="!locationHasDepartments(location.locationId)"
              ></DeleteFkEntityDialog>
              <v-tooltip top v-else>
                <template v-slot:activator="{ on }">
                  <div v-on="on">
                    <v-btn
                      :data-cy="`deleteBtn--location${location.locationId}`"
                      :disabled="deleteLocationIsDisabled(location.locationId)"
                      id
                      color="success darken-4"
                      small
                      text
                      >Delete</v-btn
                    >
                  </div>
                </template>
                <span>
                  Cannot delete location when it has departments, the structure
                  is locked, or the build is complete.
                </span>
              </v-tooltip>
            </v-card-actions>
          </v-card>
        </div>
        <div>
          <v-card
            :data-cy="`locationRow${location.locationId}DepartmentRow`"
            v-for="department in getDepartmentListByLocationId(
              'locationId',
              location.locationId
            )"
            :key="department.departmentId"
            class="och-card--structure pt-2 mb-5 caption elevation-0"
            color="primary"
            dark
            shaped
            tile
          >
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <div v-on="on">
                  <div
                    class="font-weight-bold subtitle-1"
                    :data-cy="`departmentName${department.departmentId}`"
                  >
                    {{ department.name }}
                  </div>
                  <div
                    class="font-weight-bold subtitle-1"
                    v-if="department.departmentNumber"
                  >
                    {{ department.departmentNumber }}
                  </div>
                  <div v-else>
                    <span class="font-italic subtitle-1"
                      >(Missing Location #)</span
                    >
                  </div>
                  <div
                    class="font-weight-bold subtitle-2"
                    v-if="department.abbreviation"
                  >
                    {{ `${department.abbreviation}`.toUpperCase() }}
                  </div>
                  <div v-else class="font-italic subtitle-1">
                    (Missing Abbreviation)
                  </div>
                </div>
              </template>
              <span>
                Created on {{ formatTimestamp(department.created) }}
              </span>
            </v-tooltip>
            <v-card-actions class="py-0 pb-2 pt-2 mt-3 primary darken-2">
              <v-btn
                :data-cy="`departmentEditBtn${department.departmentId}`"
                :to="{
                  name: 'departmentEdit',
                  params: {
                    id: department.departmentId,
                  },
                  query: { redirect: 'structureChart' },
                }"
                color="primary darken-2"
                elevation="0"
                small
                >Edit</v-btn
              >
              <v-spacer></v-spacer>
              <DeleteFkEntityDialog
                :disabled="isDisabled"
                :fkEntityId="department.departmentId"
                :fkEntityLabel="department.name"
                :outlined="false"
                :text="true"
                color="white"
                fkEntityName="department"
              ></DeleteFkEntityDialog>
            </v-card-actions>
          </v-card>
        </div>
        <v-card
          class="elevation-0 body-1 py-2 mt-9"
          color="transparent"
          tile
          v-if="canCreateDepartment"
        >
          <v-btn
            :data-cy="`locationRow${location.locationId}DepartmentAddBtn`"
            :disabled="isDisabled"
            @click="navigateToCreateDepartment()"
            class="pt-3 ml-3"
            icon
          >
            <v-icon x-large>fas fa-plus-circle</v-icon>
          </v-btn>
        </v-card>
      </div>
      <v-divider class="my-5"></v-divider>
    </div>
    <div>
      <v-card
        class="och-card--structure elevation-0 py-2"
        color="transparent"
        shaped
      >
        <v-btn
          :disabled="isDisabled"
          @click="navigateToCreate"
          color="primary"
          data-cy="addLocation"
          outlined
        >
          Add Location
        </v-btn>
      </v-card>
    </div>
  </v-container>
  <generic-loader v-else></generic-loader>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import { formatTimestamp } from "@/lib/componentUtil";

import Breadcrumbs from "@/views/Breadcrumbs";
import DeleteFkEntityDialog from "@/components/DeleteFkEntityDialog";
import GenericLoader from "@/components/GenericLoader";

import BillingAndRoiBuilder from "./BillingAndRoiBuilder";
import LockStructureCheckbox from "./LockStructureCheckbox";

export default {
  components: {
    BillingAndRoiBuilder,
    Breadcrumbs,
    DeleteFkEntityDialog,
    GenericLoader,
    LockStructureCheckbox,
  },
  computed: {
    buildIsClosed() {
      return this.organizationStatusIsGreaterThanOrEqualTo("buildClosed");
    },
    isDisabled() {
      if (this.buildIsClosed) {
        return this.buildIsClosed;
      }
      return this.structureIsLocked;
    },
    structureIsLocked() {
      const structureIsLocked = this.getStructureStatus;
      return structureIsLocked;
    },
    ...mapGetters({
      getDepartmentListByLocationId: "department/filterByAttributeValue",
      getStructureStatus: "organizationStatus/getStructureStatus",
      organizationStatusIsGreaterThanOrEqualTo:
        "organizationStatus/organizationStatusIsGreaterThanOrEqualTo",
    }),
    ...mapState({
      canCreateDepartment: state =>
        state.session.user.permission.department.create,
      canEditOrganization: state =>
        state.session.user.permission.structureApp.updateOrganization,
      departmentList: state => state.department.list,
      locationList: state => state.location.list,
      locationPermission: state => state.session.user.permission.location,
      organization: state => state.organization.record,
      organizationId: state => state.session.user.organizationId,
    }),
  },
  async created() {
    await this.reloadData();
  },
  data() {
    return {
      departmentHeaders: [
        {
          text: "Name",
          align: "left",
          sortable: true,
          value: "name",
          width: "30%",
        },
      ],
      expanded: [],
      loadingComplete: false,
      locationHeaders: [
        {
          text: "ID",
          align: "left",
          sortable: true,
          value: "id",
        },
        {
          text: "Name",
          align: "left",
          sortable: true,
          value: "name",
        },
        {
          text: "Abbreviation",
          align: "left",
          sortable: false,
          value: "abbreviation",
        },
        {
          text: "Departments",
          align: "left",
          sortable: true,
          value: "departments",
        },
      ],
    };
  },
  methods: {
    ...mapActions({
      getDepartmentList: "department/list",
      getLocationList: "location/list",
      getOrganization: "organization/getByOrganizationId",
    }),
    deleteLocationIsDisabled(locationId) {
      if (this.isDisabled) {
        return this.isDisabled;
      }
      if (this.locationHasDepartments(locationId)) {
        return true;
      }
      return false;
    },
    locationHasDepartments(locationId) {
      const departmentList = this.getDepartmentListByLocationId(
        "locationId",
        locationId
      );

      return departmentList.length > 0;
    },
    navigateToCreate() {
      this.$router.push({
        name: "structureLocationCreate",
        query: { redirect: "structureChart" },
      });
    },
    navigateToCreateDepartment() {
      this.$router.push({
        name: "departmentCreate",
        query: { redirect: "structureChart" },
      });
    },
    async reloadData() {
      this.loadingComplete = false;
      try {
        await Promise.all([
          this.getDepartmentList({ reload: false }),
          this.getLocationList({ reload: false }),
          this.getOrganization(this.organizationId),
        ]);

        this.locationList.forEach(location => {
          const { locationId } = location;
          location.departmentList = this.getDepartmentListByLocationId(
            "locationId",
            locationId
          );
        });
      } catch (e) {
        console.error(e);
      }
      this.loadingComplete = true;
    },
    formatTimestamp,
  },
};
</script>
<style>
.och-button--short {
  height: 35px !important;
}

.och-card--structure {
  text-align: center;
  width: 175px !important;
  max-width: 175px !important;
  display: inline-block !important;
  margin-right: 1rem !important;
  border-radius: 10px !important;
}

.och-card--structure a {
  color: white !important;
  text-decoration: none !important;
}
</style>
